// Stato di navigazione del negozio personale (modalità negozio) e attribuzione
// dei clienti portati dall'artigiano.
//
// - Modalità negozio: si entra dal link della bottega (?ref=<slug>) o dal pulsante
//   "Entra nel negozio" (?negozio=1). Vale per la sessione del browser e si esce
//   tornando a navigare il marketplace.
// - Attribuzione: solo dal link della bottega e solo se il cliente non aveva già
//   navigato su Rarifex (regola del primo contatto). Il backend firma un token
//   valido 30 giorni, che il carrello invia al pagamento.

const SHOP_KEY = 'rarifex_shop_mode';
const SEEN_KEY = 'rarifex_marketplace_seen';
const ATTR_KEY = 'rarifex_shop_attr';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const safe = (fn, fallback = null) => {
  try { return fn(); } catch (_) { return fallback; }
};

export const getShopSession = () => safe(() => sessionStorage.getItem(SHOP_KEY));
export const enterShop = (slug) => safe(() => sessionStorage.setItem(SHOP_KEY, slug));
export const exitShop = () => safe(() => sessionStorage.removeItem(SHOP_KEY));

export const hasSeenMarketplace = () => !!safe(() => localStorage.getItem(SEEN_KEY));
export const markMarketplaceSeen = () => {
  if (!hasSeenMarketplace()) safe(() => localStorage.setItem(SEEN_KEY, String(Date.now())));
};

const readAttributions = () => safe(() => JSON.parse(localStorage.getItem(ATTR_KEY) || '{}'), {}) || {};

const writeAttributions = (map) => safe(() => localStorage.setItem(ATTR_KEY, JSON.stringify(map)));

// Token validi (non scaduti) da inviare al pagamento
export const getAttributionTokens = () => {
  const now = Date.now() / 1000;
  const map = readAttributions();
  const valid = Object.fromEntries(Object.entries(map).filter(([, v]) => v && v.token && v.expires_at > now));
  if (Object.keys(valid).length !== Object.keys(map).length) writeAttributions(valid);
  return Object.values(valid).map((v) => v.token);
};

// Token valido per una bottega precisa (richieste su misura dirette)
export const getAttributionTokenFor = (crafterId) => {
  const v = readAttributions()[crafterId];
  return v && v.token && v.expires_at > Date.now() / 1000 ? v.token : null;
};

export const isAttributedTo = (crafterId) => {
  const v = readAttributions()[crafterId];
  return !!(v && v.expires_at > Date.now() / 1000);
};

// Chiede al backend il token per la bottega del link, se vale la regola del primo contatto
export const requestAttribution = async (slug) => {
  if (!slug || hasSeenMarketplace()) return null;
  try {
    const res = await fetch(`${API_URL}/api/shops/attribution`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ref: slug }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const map = readAttributions();
    map[data.crafter_id] = { token: data.token, expires_at: data.expires_at, slug };
    writeAttributions(map);
    return data;
  } catch (_) {
    return null;
  }
};

// Pagine Rarifex che, con il negozio aperto, vengono mostrate DENTRO il negozio
// (intestazione e stile della bottega): carrello e pagamento, accesso, conferma
// ordine, messaggi, assistenza e pagine informative.
const EMBEDDED_PREFIXES = [
  '/cart', '/order-success', '/login', '/signup', '/auth/', '/messages', '/assistenza-ordine',
  '/termini', '/privacy', '/cookie', '/resi-rimborsi', '/spedizioni', '/pagamenti', '/come-acquistare', '/faq',
];
export const isNeutralPath = (pathname) =>
  EMBEDDED_PREFIXES.some((p) => (p.endsWith('/') ? pathname.startsWith(p) : pathname === p || pathname.startsWith(`${p}/`)));

// Rimandi al marketplace che, dentro il negozio, portano alla pagina equivalente della bottega
export const remapToShop = (pathname, slug) => {
  const paths = shopPaths(slug);
  if (['/', '/home'].includes(pathname)) return paths.home;
  if (['/products', '/esplora', '/categorie', '/crafters'].includes(pathname) || pathname.startsWith('/categoria/')) return paths.page('prodotti');
  if (pathname === '/prodotto-su-misura' || pathname === '/requests') return paths.page('su-misura');
  const m = pathname.match(/^\/prodotto\/([^/]+)$/);
  if (m) return paths.product(m[1]);
  return null;
};

export const shopPaths = (slug) => ({
  home: `/artigiano/${slug}`,
  page: (page) => `/artigiano/${slug}/${page}`,
  product: (p) => `/artigiano/${slug}/prodotto/${typeof p === 'string' ? p : (p?.slug || p?.id)}`,
});

// Link breve del negozio: <sito>/@<slug> (middleware.js lo porta al negozio con attribuzione)
export const shopShortUrl = (slug) => {
  const label = String(slug || '').toLowerCase();
  if (!/^[a-z0-9-]{1,80}$/.test(label)) return null;
  return `${window.location.origin}/@${label}`;
};

// Riserva lato client (es. sviluppo locale, dove middleware.js non gira)
export const shortLinkPath = (pathname, search) => {
  const m = String(pathname || '').match(/^\/@([a-z0-9-]{1,80})(\/.*)?$/i);
  if (!m) return null;
  const slug = m[1].toLowerCase();
  const rest = (m[2] || '').replace(/\/+$/, '');
  const q = new URLSearchParams(search || '');
  const via = q.get('via') || 'breve';
  return `/artigiano/${slug}${rest}?ref=${encodeURIComponent(slug)}&via=${encodeURIComponent(via)}`;
};
